import { useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { IconButton } from '@features/shared/components/IconButton'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import { EditIcon, SpinnerIcon } from '@features/shared/icons/icons'
import { UserAvatar } from '@features/shared/components/UserAvatar'
import { getUserInitials } from '@features/shared/utils/userInitials'

/** The Me header's photo, editable in place through the same Clerk upload as Profile. */
export function AccountPhotoEditor({ onEdit }: { onEdit?: () => void }) {
  const { user } = useUser()
  const { addToast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const initials = getUserInitials(
    user?.firstName,
    user?.lastName,
    user?.primaryEmailAddress?.emailAddress
  )

  const uploadPhoto = async (file: File | undefined) => {
    if (!file || !user || uploading) return
    setUploading(true)
    try {
      await user.setProfileImage({ file })
      addToast({ type: 'success', title: 'Photo updated' })
    } catch {
      addToast({ type: 'error', title: 'Photo could not be updated', message: 'Please try again.' })
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <IconButton
        label="Change photo"
        wrapperClassName="shrink-0"
        className="group/avatar relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-[20%] text-[color:var(--avatar-placeholder-text)] outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-info)]"
        disabled={!user || uploading}
        disabledReason={uploading ? 'Uploading photo…' : undefined}
        aria-busy={uploading}
        onClick={() => {
          onEdit?.()
          fileInputRef.current?.click()
        }}
      >
        <UserAvatar imageUrl={user?.hasImage ? user.imageUrl : null} initials={initials} />
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center rounded-[inherit] bg-[color:var(--avatar-edit-overlay)] transition-opacity ${uploading ? 'opacity-100' : 'opacity-0 group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100'}`}
        >
          {uploading ? <SpinnerIcon size={16} /> : <EditIcon size={15} />}
        </span>
      </IconButton>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        aria-label="Choose profile photo"
        className="hidden"
        disabled={uploading}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0]
          event.currentTarget.value = ''
          void uploadPhoto(file)
        }}
      />
    </>
  )
}
